import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { 
  FiMessageSquare, 
  FiPaperclip, 
  FiCornerDownLeft 
} from "react-icons/fi";
import { getSafeSession } from "../../supabaseClient";
import "./SupportMessagesAdmin.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function SupportMessagesAdmin() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, open, replied, closed
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);

  const getHeaders = async () => {
    try {
      const result = await getSafeSession(15000);
      const session = result.data?.session;
      if (session?.access_token) {
        return {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        };
      }
    } catch (e) {
      console.error("Error getting session in getHeaders:", e);
    }

    // Fallback: Read directly from LocalStorage if Supabase hangs or is slow
    try {
      const storageKey = Object.keys(localStorage).find(
        (key) => key.startsWith("sb-") && key.endsWith("-auth-token"),
      );
      if (storageKey) {
        const localData = JSON.parse(localStorage.getItem(storageKey));
        if (localData && localData.access_token) {
          return {
            headers: {
              Authorization: `Bearer ${localData.access_token}`,
            },
          };
        }
      }
    } catch (e) {
      console.error("LocalStorage fallback error in getHeaders:", e);
    }

    return {};
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const headers = await getHeaders();
      const res = await axios.get(`${API_URL}/admin/support/messages`, headers);
      if (res.data && res.data.status === "success") {
        setTickets(res.data.data);
        
        // Refresh selected ticket detail
        if (selectedTicket) {
          const updated = res.data.data.find(t => t.id === selectedTicket.id);
          if (updated) {
            setSelectedTicket(updated);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching admin support messages:", err);
      toast.error("Failed to load support messages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();

    // Auto-refresh every 20 seconds
    const interval = setInterval(() => {
      fetchTickets();
    }, 20000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket?.id]);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;

    try {
      setSubmittingReply(true);
      const headers = await getHeaders();
      const res = await axios.post(
        `${API_URL}/admin/support/messages/${selectedTicket.id}/reply`,
        { text: replyText },
        headers
      );

      if (res.data && res.data.status === "success") {
        setReplyText("");
        toast.success("Reply sent!");
        fetchTickets();
      }
    } catch (err) {
      console.error("Error sending admin reply:", err);
      toast.error("Failed to send reply.");
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      const headers = await getHeaders();
      const res = await axios.put(
        `${API_URL}/admin/support/messages/${selectedTicket.id}/status`,
        { status: newStatus },
        headers
      );

      if (res.data && res.data.status === "success") {
        toast.success(`Ticket status updated to ${newStatus}`);
        fetchTickets();
      }
    } catch (err) {
      console.error("Error updating ticket status:", err);
      toast.error("Failed to update status.");
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Filter & Search Tickets
  const filteredTickets = tickets.filter(t => {
    const matchesSearch = 
      t.user_email.toLowerCase().includes(searchQuery.toLowerCase()) || 
      t.subject.toLowerCase().includes(searchQuery.toLowerCase()) || 
      t.message.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filterStatus === "all") return matchesSearch;
    return t.status === filterStatus && matchesSearch;
  });

  return (
    <div className="admin-support-container">
      <div className="admin-support-header">
        <h2>Client Support Messages</h2>
        <p>Manage, reply, and resolve support requests submitted by users.</p>
      </div>

      <div className="admin-support-split">
        {/* Left Pane: Ticket List */}
        <div className="admin-support-list-pane">
          <div className="list-pane-filter">
            <input
              type="text"
              placeholder="Search by email, subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="filter-tabs">
              {["all", "open", "replied", "closed"].map((status) => (
                <button
                  key={status}
                  className={`filter-tab-btn ${filterStatus === status ? "active" : ""}`}
                  onClick={() => setFilterStatus(status)}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          <div className="admin-ticket-list">
            {filteredTickets.map((t) => (
              <div
                key={t.id}
                className={`admin-ticket-item ${selectedTicket?.id === t.id ? "active" : ""}`}
                onClick={() => setSelectedTicket(t)}
              >
                <div className="admin-ticket-meta">
                  <span className="admin-ticket-email">{t.user_email}</span>
                  <span className={`status-badge ${t.status}`}>{t.status}</span>
                </div>
                <div className="admin-ticket-subject">{t.subject}</div>
                <div className="admin-ticket-preview">{t.message}</div>
                <div style={{ fontSize: '10px', color: '#64748b', marginTop: '6px', textAlign: 'right' }}>
                  {formatDate(t.created_at)}
                </div>
              </div>
            ))}

            {loading && tickets.length === 0 ? (
              <div style={{ textAlign: "center", color: "#64748b", padding: "40px 0" }}>
                Loading support messages...
              </div>
            ) : filteredTickets.length === 0 ? (
              <div style={{ textAlign: "center", color: "#64748b", padding: "40px 0" }}>
                No support messages found.
              </div>
            ) : null}
          </div>
        </div>

        {/* Right Pane: Conversation Details */}
        <div className="admin-support-detail-pane">
          {selectedTicket ? (
            <>
              {/* Header */}
              <div className="detail-pane-header">
                <div>
                  <h3 style={{ fontSize: "16px", fontWeight: 600 }}>{selectedTicket.subject}</h3>
                  <p>Client: {selectedTicket.user_email}</p>
                </div>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <span className={`status-badge ${selectedTicket.status}`}>
                    {selectedTicket.status}
                  </span>
                  
                  {selectedTicket.status !== "closed" ? (
                    <button 
                      className="filter-tab-btn" 
                      style={{ borderColor: "#ef4444", color: "#ef4444" }}
                      onClick={() => handleUpdateStatus("closed")}
                    >
                      Close Ticket
                    </button>
                  ) : (
                    <button 
                      className="filter-tab-btn" 
                      style={{ borderColor: "#10b981", color: "#10b981" }}
                      onClick={() => handleUpdateStatus("open")}
                    >
                      Reopen Ticket
                    </button>
                  )}
                </div>
              </div>

              {/* Message Thread */}
              <div className="detail-conversation-area">
                <div className="admin-question-box">
                  <h4>User Question ({formatDate(selectedTicket.created_at)}):</h4>
                  <p>{selectedTicket.message}</p>
                  
                  {selectedTicket.attachment_url && (
                    <div className="attachment-preview-box" style={{ background: "rgba(255,255,255,0.06)" }}>
                      <FiPaperclip size={14} />
                      <span>Attachment: </span>
                      {selectedTicket.attachment_url.startsWith("data:") ? (
                        <a href={selectedTicket.attachment_url} download={`attachment_${selectedTicket.id}`}>
                          Download Attachment
                        </a>
                      ) : (
                        <a href={selectedTicket.attachment_url} target="_blank" rel="noopener noreferrer">
                          View Attachment File
                        </a>
                      )}
                      {selectedTicket.attachment_url.startsWith("data:image/") && (
                        <img 
                          src={selectedTicket.attachment_url} 
                          alt="Attachment Preview" 
                          className="attachment-thumbnail-small"
                          style={{ maxWidth: "250px", maxHeight: "180px" }}
                        />
                      )}
                    </div>
                  )}
                </div>

                {(selectedTicket.replies || []).map((reply, idx) => (
                  <div key={idx} className={`chat-message-bubble ${reply.sender}`}>
                    <div style={{ fontWeight: 600, fontSize: '11px', marginBottom: '4px', opacity: 0.8 }}>
                      {reply.sender_name}
                    </div>
                    <div>{reply.text}</div>
                    <div className="message-meta">
                      {formatDate(reply.timestamp)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Editor */}
              {selectedTicket.status !== "closed" && (
                <div className="detail-reply-editor">
                  <textarea
                    placeholder="Type support reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendReply();
                      }
                    }}
                  />
                  <button 
                    className="admin-reply-btn" 
                    onClick={handleSendReply}
                    disabled={submittingReply}
                  >
                    <FiCornerDownLeft size={16} style={{ marginRight: '6px' }} />
                    {submittingReply ? "Sending..." : "Reply"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="detail-pane-empty">
              <FiMessageSquare size={48} style={{ marginBottom: "15px", opacity: 0.3 }} />
              <p>Select a support message from the left to view the thread.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
